import '../scss/main.scss';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { prefersReducedMotion } from './utils/motion.js';
import { initProductZoom } from './product-zoom.js';

gsap.registerPlugin(ScrollTrigger);

/* ---------------------------------------------------------------- *
 * Mobile nav toggle
 * ---------------------------------------------------------------- */
const navToggle = document.querySelector('.nav-toggle');
const siteNav = document.querySelector('.site-nav');

function setNavOpen(isOpen) {
  siteNav.classList.toggle('is-open', isOpen);
  navToggle.setAttribute('aria-expanded', String(isOpen));
  document.documentElement.style.overflow = isOpen ? 'hidden' : '';
}

navToggle?.addEventListener('click', () => {
  setNavOpen(!siteNav.classList.contains('is-open'));
});

// Tapping a link inside the mobile panel should close it, not leave it open
// behind the page that just navigated.
siteNav?.querySelectorAll('a').forEach((link) => {
  link.addEventListener('click', () => setNavOpen(false));
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && siteNav?.classList.contains('is-open')) setNavOpen(false);
});

/* ---------------------------------------------------------------- *
 * Language switcher (Polylang) dropdown
 * ---------------------------------------------------------------- */
document.querySelectorAll('.lang-switcher').forEach((switcher) => {
  const trigger = switcher.querySelector('.lang-switcher__current');
  trigger?.addEventListener('click', (e) => {
    e.stopPropagation();
    switcher.classList.toggle('is-open');
  });
});
document.addEventListener('click', () => {
  document.querySelectorAll('.lang-switcher.is-open').forEach((s) => s.classList.remove('is-open'));
});

/* ---------------------------------------------------------------- *
 * WooCommerce mini-cart drawer
 * ---------------------------------------------------------------- */
const cartDrawer = document.querySelector('.cart-drawer');
const cartBackdrop = document.querySelector('.cart-drawer__backdrop');
const cartOpeners = document.querySelectorAll('[data-cart-open]');
const cartClosers = document.querySelectorAll('[data-cart-close]');

function openCart() {
  cartDrawer?.classList.add('is-open');
  cartBackdrop?.classList.add('is-open');
  document.documentElement.style.overflow = 'hidden';
}

function closeCart() {
  cartDrawer?.classList.remove('is-open');
  cartBackdrop?.classList.remove('is-open');
  document.documentElement.style.overflow = '';
}

cartOpeners.forEach((btn) => btn.addEventListener('click', (e) => { 
  e.preventDefault(); 
  openCart(); 
}));

cartClosers.forEach((btn) => btn.addEventListener('click', closeCart));
cartBackdrop?.addEventListener('click', closeCart);

// Re-open automatically after WooCommerce's AJAX add-to-cart fires
document.body.addEventListener('added_to_cart', openCart);

// Close cart with Escape key
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && cartDrawer?.classList.contains('is-open')) {
    closeCart();
  }
});

/* ---------------------------------------------------------------- *
 * Shop: Filter toggles on mobile
 * ---------------------------------------------------------------- */
initProductZoom();

const shopSidebar = document.querySelector('.shop-sidebar');
if (shopSidebar) {
  // Add a toggle button for mobile
  const toggleBtn = document.createElement('button');
  toggleBtn.className = 'shop-sidebar-toggle';
  toggleBtn.setAttribute('aria-label', 'Toggle filters');
  toggleBtn.innerHTML = 'Filters <span class="shop-sidebar-toggle-icon">+</span>';
  
  const shopWrapper = document.querySelector('.woocommerce-wrapper');
  if (shopWrapper) {
    shopWrapper.insertBefore(toggleBtn, shopSidebar);
    
    toggleBtn.addEventListener('click', () => {
      shopSidebar.classList.toggle('is-open');
      const icon = toggleBtn.querySelector('.shop-sidebar-toggle-icon');
      icon.textContent = shopSidebar.classList.contains('is-open') ? '×' : '+';
    });
    
    // Close filters when clicking a filter link
    shopSidebar.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => {
        if (window.innerWidth <= 1024) {
          shopSidebar.classList.remove('is-open');
          toggleBtn.querySelector('.shop-sidebar-toggle-icon').textContent = '+';
        }
      });
    });
  }
}

/* ---------------------------------------------------------------- *
 * Gentle reveal-on-scroll for every interior/classic page.
 * Anything with .reveal-up fades + slides up once, no scroll-jacking.
 * This is intentionally simple — the "crazy" moves are reserved for
 * home.js / about.js on the homepage and about page only.
 * ---------------------------------------------------------------- */
if (!prefersReducedMotion()) {
  gsap.utils.toArray('.reveal-up').forEach((el) => {
    gsap.to(el, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: el,
        start: 'top 85%',
        once: true,
      },
    });
  });
} else {
  gsap.set('.reveal-up', { opacity: 1, y: 0 });
}

/* ---------------------------------------------------------------- *
 * Header background: on the homepage, wait until the full hero section
 * has scrolled past (not just 80px) before showing the blurred bar.
 * Interior pages don't have a .hero, so they fall back to the old
 * fixed-offset trigger (harmless there — .is-scrolled has no visual
 * effect on interior pages, which already have a solid header).
 * ---------------------------------------------------------------- */
const header = document.querySelector('.site-header');
const hero = document.querySelector('.hero');
if (header) {
  ScrollTrigger.create({
    trigger: hero || document.body,
    start: hero ? 'bottom top' : 'top -80',
    end: 99999,
    toggleClass: { targets: header, className: 'is-scrolled' },
  });
}

















// Initialize scroll animations for digital services page
document.addEventListener('DOMContentLoaded', () => {
    if (!document.body.classList.contains('is-digital-services')) return;

    const reveals = document.querySelectorAll('.reveal-up');
    
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry, index) => {
            if (entry.isIntersecting) {
                setTimeout(() => {
                    entry.target.style.transition = 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)';
                    entry.target.style.opacity = '1';
                    entry.target.style.transform = 'translateY(0)';
                }, index * 100);
                observer.unobserve(entry.target);
            }
        });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    reveals.forEach(el => observer.observe(el));
});